/**
 * StudioChatPanel — the Studio editor's middle column (Phase 3).
 *
 * A persistent per-clip conversation with the iterate agent. The full
 * thread loads from GET /api/clips/:id/iterate-thread on open (the
 * server keeps it in work/<clip>/studio-chat.jsonl — leave, come back,
 * it's still here). Sending a message POSTs the CURRENT edit state +
 * selection; the reply carries a validated EditPatch which the parent
 * commits through the SAME commit(patch, label, "chat") path the canvas
 * and inspector use — chat edits land in the same journal/undo stack.
 *
 * Channel-scope proposals don't auto-apply: the assistant bubble grows
 * Confirm / Just-this-clip buttons; either choice re-sends with
 * confirmMessageId and the server returns the patch marked appliable
 * (Confirm → the journal entry is tagged {scope:"channel-pending"} —
 * Phase 4 promotes those into the channel template).
 *
 * Failures surface as chat bubbles with the real reason — persisted
 * server-side when possible, local-only (and marked so) when the
 * request never landed.
 *
 * THREADS + MODEL (2026-09-15): the panel is a dumb renderer of ONE
 * conversation — `threadId` and `model` are owned by whoever hosts it
 * (StudioChatDock), passed through on every turn and re-read on change.
 * Omit both and the panel behaves exactly as it did before threads
 * existed: the server's "default" thread, the deployment's default model.
 */
import { AlertTriangle, Check, Paperclip, Send, Sparkles, Undo2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ClipEditState, EditPatch } from "../../../src/lib/clipEditState.ts";
import {
  getStudioChatThread,
  iterateClip,
  type StudioChatMessage,
} from "../api";
import { composerPlaceholder, selectionChipLabel, suggestionsFor } from "../lib/chatSuggestions";
import { fileToAttachment, imagesFromClipboard, MAX_ATTACH_IMAGES } from "../lib/imageAttach";
import type { StudioSelection } from "../lib/studioTypes";

const ACCENT = "#FFD500";

export interface StudioChatPanelProps {
  clipName: string;
  /** Which conversation to show and send to. Omitted ⇒ the server's
   *  "default" thread (every pre-threads row lives there). Changing it
   *  reloads the scrollback. */
  threadId?: string;
  /** Model to spend on for each turn. Omitted ⇒ the deployment default. */
  model?: string;
  /** Replaces the built-in title row — the dock passes its own header
   *  (threads, model picker, balance) so the panel stays ONE card. */
  header?: React.ReactNode;
  /** Fired when an assistant reply lands (money may have moved, the thread
   *  list may have a new title). The dock refreshes the balance chip and
   *  raises its unread dot from here. */
  onAssistantTurn?: () => void;
  /** Serialized into every request so the model edits the numbers the
   *  operator is looking at. */
  editState: ClipEditState;
  selection: StudioSelection;
  /** Commit an appliable patch (source "chat"). `channelPending` tags the
   *  journal entry {scope:"channel-pending"} for Phase 4 promotion. */
  onApplyPatch: (patch: EditPatch, explanation: string, channelPending: boolean) => void;
  /** The journal's LAST entry's label WHEN that entry came from chat,
   *  else null. The Undo chip only shows on the newest applied message
   *  while its explanation IS that label — the one moment the chip maps
   *  1:1 onto undoLast() (a manual edit or an undo in between hides it). */
  undoableChatLabel: string | null;
  onUndo: () => void;
  /** Navigate back to the clip page (Fix captions / Adjust / music tools) —
   *  rendered as a clickable chip when the agent declines an out-of-scope
   *  ask so the redirect is one tap, not a scavenger hunt. */
  onOpenClipTools?: () => void;
  /** Text of the selected cue — the chip QUOTES it, so a null here with a cue
   *  selected renders empty quotes. Pass the effective cue's real text. */
  selectedCueText: string | null;
  /** ✕ on the selection chip. */
  onClearSelection: () => void;
  /** The "Fix a caption" suggestion (an ACTION, not a message): the host
   *  selects the cue at the playhead and seeks to it. */
  onFixCaption: () => void;
  /** False hides caption-specific suggestions on a clip with no cues. */
  hasCues: boolean;
  /** Panel height (matches the player box on desktop). */
  height?: number | string;
}

/** An assistant turn that changed nothing and mentions pipeline territory
 *  (re-translation, speaker identity, music, trims) is a redirect — surface
 *  the destination as a button. Keyword-matched; a false positive just shows
 *  a harmless extra link. */
const REDIRECT_RE = /re-?translat|translation|fix captions|clip page|speaker (?:name|photo|identity)|music|trim|adjust panel/i;

/** A bubble that only exists client-side (network failure before the
 *  server could persist anything). */
interface LocalBubble {
  id: string;
  role: "assistant";
  content: string;
  error: true;
  local: true;
}

type Row = StudioChatMessage | LocalBubble;

function isLocal(r: Row): r is LocalBubble {
  return (r as LocalBubble).local === true;
}

function patchHasChange(p: EditPatch | undefined): boolean {
  if (!p) return false;
  const l = p.layout;
  const layoutTouched =
    !!l && ((l.caption && Object.keys(l.caption).length > 0)
      || (l.logo && Object.keys(l.logo).length > 0)
      || (l.speakerCard && Object.keys(l.speakerCard).length > 0));
  return !!layoutTouched
    || (!!p.brand && Object.keys(p.brand).length > 0)
    || (!!p.cues && p.cues.length > 0)
    || (!!p.cueSplits && p.cueSplits.length > 0);
}

export function StudioChatPanel({
  clipName,
  threadId,
  model,
  header,
  onAssistantTurn,
  editState,
  selection,
  onApplyPatch,
  undoableChatLabel,
  onUndo,
  onOpenClipTools,
  selectedCueText,
  onClearSelection,
  onFixCaption,
  hasCues,
  height = "min(64vh, 720px)",
}: StudioChatPanelProps): React.JSX.Element {
  const [rows, setRows] = useState<Row[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [pending, setPending] = useState(false);
  // Attached screenshots (downscaled data URLs) — paste/attach, agent sees them.
  const [images, setImages] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const addFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    try {
      const converted = await Promise.all(files.slice(0, MAX_ATTACH_IMAGES).map(fileToAttachment));
      setImages((prev) => [...prev, ...converted].slice(0, MAX_ATTACH_IMAGES));
    } catch {
      /* non-image on the clipboard — quietly ignore, the text path still works */
    }
  }, []);

  // Thread scrollback — refetch when the clip OR the selected thread changes.
  useEffect(() => {
    let alive = true;
    setRows([]);
    setLoaded(false);
    setLoadError(null);
    getStudioChatThread(clipName, threadId)
      .then((msgs) => { if (alive) { setRows(msgs); setLoaded(true); } })
      .catch((err: unknown) => {
        if (alive) {
          setLoadError(err instanceof Error ? err.message : String(err));
          setLoaded(true); // still allow chatting — the thread is server-side
        }
      });
    return () => { alive = false; };
  }, [clipName, threadId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [rows.length, pending]);

  /** Shared turn runner for normal sends and confirmation clicks. */
  const runTurn = useCallback(
    async (message: string, confirm?: { messageId: string; applyToChannel: boolean }) => {
      if (pending) return;
      setPending(true);
      const tempId = `local-user-${Date.now()}`;
      setRows((r) => [...r, {
        id: tempId,
        clipName,
        role: "user",
        content: message,
        createdAt: new Date().toISOString(),
      }]);
      try {
        const resp = await iterateClip(clipName, {
          message,
          editState,
          ...(threadId ? { threadId } : {}),
          ...(model ? { model } : {}),
          selection: selection.el
            ? {
                el: selection.el,
                cueIdx: selection.cueIdx,
                ...(selection.word ? { word: selection.word, wordIdx: selection.wordIdx } : {}),
              }
            : undefined,
          ...(!confirm && images.length > 0 ? { images } : {}),
          ...(confirm
            ? { confirmMessageId: confirm.messageId, confirmChannelScope: confirm.applyToChannel }
            : {}),
        });
        setRows((r) => {
          const next = r.filter((m) => m.id !== tempId);
          next.push(resp.userMessage);
          if (resp.assistantMessage) next.push(resp.assistantMessage);
          return next;
        });
        if (!confirm) setImages([]);
        const a = resp.assistantMessage;
        if (a?.applied && patchHasChange(a.patch)) {
          onApplyPatch(a.patch!, a.content, a.scope === "channel");
        }
        // A reply landed: the balance moved and this thread may have just
        // earned its title. Fired for error bubbles too — the turn still
        // reached the server.
        if (a) onAssistantTurn?.();
        return true;
      } catch (err) {
        // Nothing persisted server-side — put the text back in the box and
        // explain in-thread.
        const reason = err instanceof Error ? err.message : String(err);
        setRows((r) => [
          ...r.filter((m) => m.id !== tempId),
          {
            id: `local-err-${Date.now()}`,
            role: "assistant",
            content: `Couldn't reach the iterate agent — ${reason}. This note isn't saved to the thread.`,
            error: true,
            local: true,
          },
        ]);
        if (!confirm) setInput(message);
        return false;
      } finally {
        setPending(false);
      }
    },
    [clipName, threadId, model, editState, selection, pending, images, onApplyPatch, onAssistantTurn],
  );

  const onSend = useCallback(async () => {
    const message = input.trim();
    if ((!message && images.length === 0) || pending || !loaded) return;
    setInput("");
    await runTurn(message || "Look at the attached screenshot — fix what's shown/marked.");
  }, [input, images.length, pending, loaded, runTurn]);

  /** A suggestion chip sends its own label — it IS the message, so it never
   *  goes through the composer (nothing to clear, nothing to re-type). */
  const sendSuggestion = useCallback((text: string) => {
    if (pending || !loaded) return;
    void runTurn(text);
  }, [pending, loaded, runTurn]);

  // Confirmation state: a pending proposal is OPEN until some later
  // message resolves it (append-only thread — resolution is a new row).
  const resolvedIds = new Set(
    rows.flatMap((m) => (!isLocal(m) && m.resolvesId ? [m.resolvesId] : [])),
  );
  const lastAppliedId = [...rows].reverse().find(
    (m) => !isLocal(m) && m.applied && patchHasChange((m as StudioChatMessage).patch),
  )?.id;

  const chipLabel = selectionChipLabel(selection, selectedCueText);
  const suggestions = suggestionsFor(selection, { hasCues });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height,
        minWidth: 0,
        background: "rgba(255,255,255,.02)",
        border: "1px solid rgba(255,255,255,.06)",
        borderRadius: 16,
        padding: 14,
      }}
    >
      {header ?? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingBottom: 10 }}>
          <Sparkles size={14} color={ACCENT} />
          <span style={{ fontSize: 12.5, fontWeight: 700 }}>Design agent</span>
          <span style={{ fontSize: 11, color: "var(--ql-fg-subtle)" }}>
            sizes · positions · colors · wording · splits — previews instantly
          </span>
        </div>
      )}

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: "2px 2px 8px",
        }}
      >
        {!loaded ? (
          <div style={{ fontSize: 12, color: "var(--ql-fg-subtle)", padding: "16px 4px" }}>
            Loading conversation…
          </div>
        ) : (
          <>
            {loadError && (
              <ErrorBubble text={`Couldn't load the saved conversation: ${loadError}`} />
            )}
            {rows.length === 0 && !loadError && (
              <EmptyState />
            )}
            {rows.map((m) => (
              <MessageBubble
                key={m.id}
                row={m}
                showUndo={m.id === lastAppliedId && !isLocal(m) && m.content === undoableChatLabel}
                onUndo={onUndo}
                onOpenClipTools={onOpenClipTools}
                confirmOpen={
                  !isLocal(m)
                  && !!(m as StudioChatMessage).pendingConfirmation
                  && !resolvedIds.has(m.id)
                }
                confirmDisabled={pending}
                onConfirm={(applyToChannel) =>
                  void runTurn(
                    applyToChannel ? "Apply it to the whole channel." : "Just this clip.",
                    { messageId: m.id, applyToChannel },
                  )
                }
              />
            ))}
            {pending && (
              <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "var(--ql-fg-subtle)", padding: "2px 4px" }}>
                <Sparkles size={12} className="ql-spin" color={ACCENT} />
                thinking…
              </div>
            )}
          </>
        )}
      </div>

      {images.length > 0 && (
        <div style={{ display: "flex", gap: 6, paddingBottom: 6 }}>
          {images.map((src, i) => (
            <div key={i} style={{ position: "relative" }}>
              <img
                src={src}
                alt={`attachment ${i + 1}`}
                style={{
                  height: 48, width: 66, objectFit: "cover", borderRadius: 8,
                  border: "1px solid rgba(255,255,255,.15)", display: "block",
                }}
              />
              <button
                type="button"
                onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
                aria-label="Remove attachment"
                title="Remove"
                style={{
                  position: "absolute", top: -5, right: -5, width: 16, height: 16,
                  display: "grid", placeItems: "center", borderRadius: 999,
                  background: "rgba(16,17,22,.95)", border: "1px solid rgba(255,255,255,.25)",
                  color: "var(--ql-fg)", cursor: "pointer", padding: 0,
                }}
              >
                <X size={9} />
              </button>
            </div>
          ))}
        </div>
      )}
      {/* The selection was ALREADY on every turn — this is what makes it
          visible, so "it" in the next message is never ambiguous (spec §4). */}
      {chipLabel && (
        <div style={{ display: "flex", paddingBottom: 6 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              maxWidth: "100%",
              minWidth: 0,
              padding: "4px 5px 4px 9px",
              borderRadius: 999,
              border: "1px solid rgba(255,213,0,.35)",
              background: "rgba(255,213,0,.09)",
              color: "var(--ql-yellow)",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {chipLabel}
            </span>
            <button
              type="button"
              onClick={onClearSelection}
              aria-label="Clear selection"
              title="Talk about the whole clip instead"
              style={{
                display: "grid", placeItems: "center", width: 16, height: 16, flexShrink: 0,
                borderRadius: 999, border: "none", background: "transparent",
                color: "var(--ql-yellow)", cursor: "pointer", padding: 0,
              }}
            >
              <X size={10} />
            </button>
          </span>
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: 6,
          background: "rgba(255,255,255,.04)",
          border: "1px solid rgba(255,255,255,.10)",
          borderRadius: 12,
          padding: 4,
        }}
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onPaste={(e) => {
            const files = imagesFromClipboard(e);
            if (files.length > 0) {
              e.preventDefault();
              void addFiles(files);
            }
          }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              void onSend();
            }
          }}
          placeholder={composerPlaceholder(selection)}
          rows={2}
          disabled={pending}
          style={{
            flex: 1,
            background: "transparent",
            border: "none",
            outline: "none",
            color: "var(--ql-fg)",
            fontSize: 13,
            fontFamily: "inherit",
            resize: "none",
            padding: "7px 9px",
            lineHeight: 1.5,
            minWidth: 0,
          }}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            e.target.value = "";
            void addFiles(files);
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={pending || images.length >= MAX_ATTACH_IMAGES}
          aria-label="Attach a screenshot"
          title={images.length >= MAX_ATTACH_IMAGES ? `Max ${MAX_ATTACH_IMAGES} images` : "Attach a screenshot (or paste one)"}
          style={{
            background: "transparent",
            color: "var(--ql-fg-subtle)",
            border: "none",
            borderRadius: 9,
            padding: "0 6px",
            cursor: pending || images.length >= MAX_ATTACH_IMAGES ? "default" : "pointer",
            opacity: images.length >= MAX_ATTACH_IMAGES ? 0.4 : 1,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Paperclip size={14} />
        </button>
        <button
          type="button"
          onClick={() => void onSend()}
          disabled={pending || (!input.trim() && images.length === 0) || !loaded}
          aria-label="Send"
          style={{
            background: (input.trim() || images.length > 0) && !pending && loaded ? ACCENT : "rgba(255,255,255,.08)",
            color: (input.trim() || images.length > 0) && !pending && loaded ? "#000" : "var(--ql-fg-subtle)",
            border: "none",
            borderRadius: 9,
            padding: "0 12px",
            cursor: pending || (!input.trim() && images.length === 0) || !loaded ? "default" : "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Send size={15} />
        </button>
      </div>
      {/* ≤4 contextual chips — the blank composer is the hardest question in
          the app, and what to ask depends on what is selected. */}
      {suggestions.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, paddingTop: 7 }}>
          {suggestions.map((sg) => {
            const isAction = sg.kind === "action";
            return (
              <button
                key={sg.label}
                type="button"
                disabled={pending || !loaded}
                onClick={() => (isAction ? onFixCaption() : sendSuggestion(sg.label))}
                title={isAction ? "Select the caption on screen and edit it" : `Ask: ${sg.label}`}
                style={{
                  padding: "5px 9px",
                  borderRadius: 999,
                  border: "1px solid " + (isAction ? "rgba(255,213,0,.35)" : "rgba(255,255,255,.10)"),
                  background: isAction ? "rgba(255,213,0,.08)" : "rgba(255,255,255,.03)",
                  color: isAction ? ACCENT : "var(--ql-fg-muted)",
                  fontFamily: "inherit",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: pending || !loaded ? "default" : "pointer",
                  opacity: pending || !loaded ? 0.5 : 1,
                }}
              >
                {sg.label}
              </button>
            );
          })}
        </div>
      )}
      <div style={{ fontSize: 10.5, color: "var(--ql-fg-subtle)", textAlign: "right", paddingTop: 5 }}>
        ⌘/Ctrl + Enter to send
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function EmptyState(): React.JSX.Element {
  return (
    <div style={{ padding: "16px 8px" }}>
      <div style={{ fontSize: 12, lineHeight: 1.65, color: "var(--ql-fg-subtle)" }}>
        Describe what feels off — every change previews instantly and can be
        undone.
      </div>
    </div>
  );
}

function ErrorBubble({ text }: { text: string }): React.JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        background: "rgba(248,113,113,.07)",
        border: "1px solid rgba(248,113,113,.25)",
        borderRadius: 12,
        padding: "9px 11px",
        fontSize: 12.5,
        lineHeight: 1.55,
        color: "var(--ql-danger)",
        maxWidth: "94%",
      }}
    >
      <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
      <span style={{ minWidth: 0, wordBreak: "break-word" }}>{text}</span>
    </div>
  );
}

function MessageBubble({
  row,
  showUndo,
  onUndo,
  confirmOpen,
  confirmDisabled,
  onConfirm,
  onOpenClipTools,
}: {
  row: Row;
  showUndo: boolean;
  onUndo: () => void;
  confirmOpen: boolean;
  confirmDisabled: boolean;
  onConfirm: (applyToChannel: boolean) => void;
  onOpenClipTools?: () => void;
}): React.JSX.Element {
  if (isLocal(row) || (row as StudioChatMessage).error) {
    return <ErrorBubble text={row.content} />;
  }
  const m = row as StudioChatMessage;
  const isUser = m.role === "user";
  const applied = !!m.applied && patchHasChange(m.patch);
  const channelPending = applied && m.scope === "channel";
  // Declined/redirected turn that names pipeline territory → clickable way out.
  const showClipToolsLink =
    !isUser && !applied && !confirmOpen && !!onOpenClipTools && REDIRECT_RE.test(m.content);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start", gap: 5 }}>
      <div
        style={{
          background: isUser ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.04)",
          border: `1px solid ${isUser ? "rgba(255,213,0,.22)" : "rgba(255,255,255,.07)"}`,
          borderRadius: 12,
          padding: "9px 11px",
          fontSize: 13,
          lineHeight: 1.55,
          maxWidth: "94%",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          color: "var(--ql-fg)",
        }}
      >
        {m.images && m.images.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 7 }}>
            {m.images.map((src, i) => (
              <img
                key={i}
                src={src}
                alt={`screenshot ${i + 1}`}
                style={{
                  maxHeight: 110, maxWidth: 160, borderRadius: 8,
                  border: "1px solid rgba(255,255,255,.15)", display: "block", cursor: "zoom-in",
                }}
                onClick={() => window.open(src, "_blank")}
              />
            ))}
          </div>
        )}
        {m.content}
      </div>

      {showClipToolsLink && (
        <button
          type="button"
          onClick={onOpenClipTools}
          title="Back to the clip page — the Fix captions agent and Adjust panel live there"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 11, fontWeight: 700, fontFamily: "inherit",
            color: "var(--ql-yellow)", background: "rgba(255,213,0,.08)",
            border: "1px solid rgba(255,213,0,.3)", borderRadius: 999,
            padding: "4px 11px", cursor: "pointer",
          }}
        >
          ↩ Open the clip page — Fix captions &amp; Adjust
        </button>
      )}

      {applied && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 2 }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: ".05em",
              textTransform: "uppercase",
              color: "#63E2B7",
              background: "rgba(99,226,183,.10)",
              border: "1px solid rgba(99,226,183,.25)",
              borderRadius: 999,
              padding: "2px 8px",
            }}
          >
            <Check size={10} strokeWidth={3} />
            {channelPending ? "applied · channel-pending" : "applied"}
          </span>
          {showUndo && (
            <button
              type="button"
              onClick={onUndo}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 10.5,
                fontWeight: 600,
                color: "var(--ql-fg-muted)",
                background: "rgba(255,255,255,.04)",
                border: "1px solid rgba(255,255,255,.12)",
                borderRadius: 999,
                padding: "2px 9px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
              title="Undo this change (pops the journal)"
            >
              <Undo2 size={10} /> Undo
            </button>
          )}
        </div>
      )}

      {confirmOpen && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 7,
            background: "rgba(255,213,0,.06)",
            border: "1px solid rgba(255,213,0,.25)",
            borderRadius: 12,
            padding: "8px 10px",
            maxWidth: "94%",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--ql-fg)", fontWeight: 600 }}>
            Apply to the whole channel?
          </span>
          <button
            type="button"
            disabled={confirmDisabled}
            onClick={() => onConfirm(true)}
            style={confirmBtnStyle(true, confirmDisabled)}
          >
            Confirm — whole channel
          </button>
          <button
            type="button"
            disabled={confirmDisabled}
            onClick={() => onConfirm(false)}
            style={confirmBtnStyle(false, confirmDisabled)}
          >
            Just this clip
          </button>
        </div>
      )}
    </div>
  );
}

function confirmBtnStyle(primary: boolean, disabled: boolean): React.CSSProperties {
  return {
    fontSize: 11.5,
    fontWeight: 700,
    fontFamily: "inherit",
    borderRadius: 999,
    padding: "4px 11px",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.5 : 1,
    border: `1px solid ${primary ? ACCENT : "rgba(255,255,255,.15)"}`,
    background: primary ? "rgba(255,213,0,.14)" : "rgba(255,255,255,.04)",
    color: primary ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
  };
}
